package com.partejar.app.feature.auth.presentation

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.input.PasswordVisualTransformation
import com.partejar.app.core.ui.components.passwordKeyboardOptions
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import com.partejar.app.core.session.SessionDestination
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.components.PartejarThemeToggleAction
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import org.jetbrains.compose.resources.painterResource
import org.koin.compose.koinInject
import partejar_app.shared.generated.resources.Res
import partejar_app.shared.generated.resources.partejar_lockup_dark_transparent
import partejar_app.shared.generated.resources.partejar_lockup_light_transparent

@Composable
fun AuthScreen(
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    viewModel: AuthViewModel = koinInject(),
    onDestinationResolved: (SessionDestination) -> Unit,
    onAuthenticated: (() -> Unit)? = null
) {
    val state by viewModel.state.collectAsState()
    val spacing = PartejarTheme.spacing
    var passwordVisible by remember { mutableStateOf(false) }

    LaunchedEffect(state.destination) {
        state.destination?.let { destination ->
            onAuthenticated?.invoke() ?: onDestinationResolved(destination)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        contentColor = MaterialTheme.colorScheme.onBackground
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .statusBarsPadding()
                .imePadding()
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(
                        horizontal = spacing.space24,
                        vertical = spacing.space32
                    ),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Image(
                        painter = painterResource(
                            if (isDarkTheme) Res.drawable.partejar_lockup_dark_transparent else Res.drawable.partejar_lockup_light_transparent
                        ),
                        contentDescription = null,
                        contentScale = ContentScale.FillWidth,
                    )
                    Spacer(modifier = Modifier.height(spacing.space40))

                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(spacing.space8)
                    ) {
                        Text(
                        text = if (state.isRegistration) "Crie sua conta" else "Boas-vindas de volta",
                            style = MaterialTheme.typography.headlineSmall,
                            color = MaterialTheme.colorScheme.onBackground
                        )

                        Text(
                        text = if (state.isRegistration) "Comece criando seu acesso ao Partejar." else "Entre para acessar seu espaço.",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    Spacer(modifier = Modifier.padding(top = spacing.space24))

                    if (state.isRegistration) {
                        PartejarTextField(
                            value = state.nome,
                            onValueChange = { viewModel.onAction(AuthAction.OnNomeChanged(it)) },
                            label = "Nome",
                            isError = state.erro != null,
                            enabled = !state.isLoading
                        )
                        Spacer(modifier = Modifier.padding(top = spacing.space16))
                    }

                    PartejarTextField(
                        value = state.email,
                        onValueChange = {
                            viewModel.onAction(AuthAction.OnEmailChanged(it))
                        },
                        label = "E-mail",
                        isError = state.erro != null,
                        enabled = !state.isLoading
                    )

                    Spacer(modifier = Modifier.padding(top = spacing.space16))

                    PartejarTextField(
                        value = state.senha,
                        onValueChange = {
                            viewModel.onAction(AuthAction.OnSenhaChanged(it))
                        },
                        label = "Senha",
                        keyboardOptions = passwordKeyboardOptions,
                        visualTransformation = if (passwordVisible) {
                            VisualTransformation.None
                        } else {
                            PasswordVisualTransformation()
                        },
                        isError = state.erro != null,
                        enabled = !state.isLoading,
                        trailingIcon = {
                            IconButton(
                                onClick = {
                                    passwordVisible = !passwordVisible
                                },
                                enabled = !state.isLoading
                            ) {
                                Icon(
                                    imageVector = if (passwordVisible) {
                                        PartejarIcons.Actions.hidePassword
                                    } else {
                                        PartejarIcons.Actions.showPassword
                                    },
                                    contentDescription = if (passwordVisible) {
                                        "Ocultar senha"
                                    } else {
                                        "Mostrar senha"
                                    }
                                )
                            }
                        }
                    )

                    if (state.isRegistration) {
                        Spacer(modifier = Modifier.padding(top = spacing.space16))
                        PartejarTextField(
                            value = state.confirmacaoSenha,
                            onValueChange = { viewModel.onAction(AuthAction.OnConfirmacaoSenhaChanged(it)) },
                            label = "Confirmar senha",
                            keyboardOptions = passwordKeyboardOptions,
                            visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                            isError = state.erro != null,
                            enabled = !state.isLoading
                        )
                    }

                    state.erro?.let { erro ->
                        Spacer(modifier = Modifier.padding(top = spacing.space8))

                        Text(
                            text = erro,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.error,
                            modifier = Modifier.align(Alignment.Start)
                        )
                    }

                    Spacer(modifier = Modifier.padding(top = spacing.space24))

                    PartejarButton(
                        text = if (state.isRegistration) "Criar conta" else "Entrar",
                        onClick = {
                            viewModel.onAction(if (state.isRegistration) AuthAction.OnRegisterClicked else AuthAction.OnLoginClicked)
                        },
                        isLoading = state.isLoading
                    )

                    TextButton(
                        onClick = { viewModel.onAction(AuthAction.OnRegistrationModeToggled) },
                        enabled = !state.isLoading,
                        modifier = Modifier.align(Alignment.CenterHorizontally)
                    ) {
                        Text(if (state.isRegistration) "Já tenho uma conta" else "Criar conta")
                    }
                }
            }

            PartejarThemeToggleAction(
                isDarkTheme = isDarkTheme,
                onClick = {
                    onThemeToggle()
                },
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(
                        top = spacing.space8,
                        end = spacing.space16
                    )
            )
        }
    }
}
